import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, X, BellRing, Check } from "lucide-react";
import { getNotifications, markNotifsRead, getBadges } from "@/lib/api";
import { pushSupported, currentPushState, enablePush } from "@/lib/push";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function NotificationBell({ fixed = true }) {
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState("off");
  const { data: badges, refetch: refetchBadges } = useQuery({ queryKey: ["badges"], queryFn: getBadges, refetchInterval: 45000 });
  const { data: notifs = [], refetch: refetchNotifs } = useQuery({ queryKey: ["notifications"], queryFn: getNotifications, enabled: open });

  const unread = badges?.notif_unread || 0;

  useEffect(() => { if (pushSupported()) currentPushState().then(setPushState); }, []);

  const openPanel = async () => {
    setOpen(true);
    await refetchNotifs();
    if (unread > 0) { await markNotifsRead(); refetchBadges(); }
  };

  const activarPush = async () => {
    try {
      await enablePush();
      setPushState("on");
      toast.success("Avisos activados en este dispositivo ✓");
    } catch (e) {
      if (e.message === "denied") toast.error("Has bloqueado las notificaciones. Actívalas en los ajustes del navegador.");
      else if (e.message === "unsupported") toast.error("Tu navegador no admite notificaciones push.");
      else toast.error("No se pudieron activar los avisos.");
    }
  };

  return (
    <>
      <button className={`notif-bell ${fixed ? "" : "inline"}`} data-testid="notif-bell" onClick={openPanel} aria-label="Avisos">
        <Bell size={19} />
        {unread > 0 && <span className="notif-count" data-testid="notif-count">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="notif-overlay" onClick={() => setOpen(false)}>
          <div className="notif-panel" data-testid="notif-panel" onClick={(e) => e.stopPropagation()}>
            <div className="notif-head">
              <b>Avisos</b>
              <button className="install-close" onClick={() => setOpen(false)} aria-label="Cerrar"><X size={16} /></button>
            </div>

            {pushSupported() && pushState !== "on" && (
              <button className="notif-push-cta" data-testid="notif-activar-push" onClick={activarPush}>
                <BellRing size={15} /> Activar avisos en este dispositivo
              </button>
            )}
            {pushState === "on" && (
              <div className="notif-push-on" data-testid="notif-push-activo"><Check size={14} /> Avisos activados en este dispositivo</div>
            )}

            <div className="notif-list">
              {notifs.length === 0 ? (
                <p className="notif-empty" data-testid="notif-vacio">No tienes avisos por ahora.</p>
              ) : (
                notifs.map((n) => (
                  <div className={`notif-item ${n.nueva ? "nueva" : ""}`} key={n.id} data-testid={`notif-item-${n.id}`}>
                    <div className="notif-item-title">{n.titulo}</div>
                    {n.cuerpo && <div className="notif-item-body">{n.cuerpo}</div>}
                    <div className="notif-item-date">{fmt(n.created_at)}</div>
                    {n.url && (
                      <button className="notif-item-cta" data-testid={`notif-ver-${n.id}`}
                        onClick={() => { setOpen(false); window.location.assign(n.url); }}>
                        Ver contenido →
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
